/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ConnectionPositionPair } from '@angular/cdk/overlay';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  AbstractFormComponent,
  BuilderUserContext,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
} from '@gh/builder-shared';
import { EmailTemplateType } from '@gh/builder-shared-data';
import { ActionFlow, AppRouter, getRouteInitData, HelpID } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import {
  AutocompleteLoadMoreEvent,
  DialogService,
  FormMode,
  getControlValue,
  TinymceSettings,
  TinymceTextareaDirective,
  tmSettingsBuilder,
  UploadContext,
} from '@gh/core-ui';

import { assign, isNil, omit } from 'lodash';

import {
  EmailTemplateDetailsBusinessService,
  EmailTemplatePreviewDialogConfig,
} from '../../resources/email-template-details-business.service';
import {
  EmailPlaceholder,
  EmailTemplateDetails,
  PLACEHOLDER_CODE_SIGN,
} from '../../resources/email-template.model';
import { EmailTemplatePreviewComponent } from '../email-template-preview/email-template-preview.component';
import { EmailTemplateDetailsFormState } from './email-template-details-form.service';
import { Validators } from '@angular/forms';

@Component({
  standalone: false,
  selector: 'gh-email-template-details',
  templateUrl: 'email-template-details.component.html',
  styleUrls: ['email-template-details.component.scss'],
  viewProviders: [EmailTemplateDetailsFormState, UploadContext],
})
export class EmailTemplateDetailsComponent extends AbstractFormComponent<EmailTemplateDetails> implements OnInit, AfterViewInit {
  isPlaceholderAutocompleteOpen: boolean = false;
  canOverwrite: boolean = false;
  backUrlTitle: string = 'lbl_manage_email_templates';
  header: string = '';
  footer: string = '';
  officeId: Maybe<ID>;
  masterZoneId: Maybe<ID>;
  type: Maybe<EmailTemplateType>;
  tmSettings: TinymceSettings;
  body: string;
  private isInitializing: boolean = true;

  @ViewChild(TinymceTextareaDirective, { static: false }) editor: TinymceTextareaDirective;
  @ViewChild('nameField') fieldToFocus!: ElementRef;

  constructor(private route: ActivatedRoute,
              private router: AppRouter,
              private businessService: EmailTemplateDetailsBusinessService,
              public form: EmailTemplateDetailsFormState,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              private userContext: BuilderUserContext,
              private dialogService: DialogService,
              private cdr: ChangeDetectorRef) {
    super();
  }

  get helpId(): HelpID {
    return {
      [EmailTemplateType.System]: 'setup/email-templates/system/details',
      [EmailTemplateType.Job]: 'setup/email-templates/job-letters/details',
      [EmailTemplateType.Internal]: 'setup/email-templates/internal/details',
      [EmailTemplateType.Layout]: 'setup/email-templates/layout/details',
    }[this.form.type];
  }

  /**
   * @returns {any}
   */
  get pageTitle(): string {
    return {
      [FormMode.CreateMode]: 'lbl_template_create',
      [FormMode.EditMode]: 'lbl_template_edit',
      [FormMode.ViewMode]: '',
    }[this.form.mode];
  }

  get completeFlow(): ActionFlow<FormFlowState<EmailTemplateDetails>> {
    return this.pipelines.complete({
      execute: this.commonActions.execute((state: FormFlowState<EmailTemplateDetails>) =>
        this.businessService.save(this.addPlaceholdersToState(this.removeTechFields(state.form.submitValue)))),
      finish: () => {
        this.router.navigateBack();
      },
    });
  }

  get placeholderOverlayPositions(): ConnectionPositionPair[] {
    return [
      new ConnectionPositionPair(
        { originX: 'end', originY: 'center' },
        { overlayX: 'start', overlayY: 'center' }),
      new ConnectionPositionPair(
        { originX: 'start', originY: 'bottom' },
        { overlayX: 'end', overlayY: 'top' }),
    ];
  }

  ngOnInit(): void {
    const { emailTemplate, ...routeInitData } = getRouteInitData(this.route);
    this.body = emailTemplate.body;
    this.form.build(emailTemplate);
    const { header, footer, masterZoneId, officeId, type } = (this.form.mode === FormMode.CreateMode)
      ? routeInitData.emailTemplateInfo
      : emailTemplate;
    this.header = header;
    this.footer = footer;
    this.masterZoneId = masterZoneId;
    this.officeId = officeId;
    this.type = type;
    this.canOverwrite = emailTemplate.id && this.checkCanOverwrite(emailTemplate);
    this.backUrlTitle = this.businessService.getBackUrlTitle(emailTemplate);

    // images cannot be used under master view for layout templates
    const canUseImages = !(this.userContext.masterView && this.form.type === EmailTemplateType.Layout);
    const builder = tmSettingsBuilder()
      .images(canUseImages)
      .scayt()
      .height('100%')
      .styles(emailTemplate.type === EmailTemplateType.Layout
        ? `body { width: 620px; }`
        : `body { padding: 0 40px; width: 540px; }`)
      .build();
    this.tmSettings = {
      ...builder,
      onChange: (event: any) => this.onDescriptionChange(event),
      onPaste: (event: any) => this.onDescriptionChange(event),
      onLoadContent: (editor: any) => {
        setTimeout(() => {
          const text = editor.getContent({format: 'text'});
          if (!this.form) {
            return;
          }
          const control = this.form.getControl('bodyText');
          control.setValue(text);
          control.setValidators([Validators.required]);
          this.isInitializing = false;
          this.cdr.markForCheck();
        }, 300);

      },
      onMouseleave: (editor: any) => {
        this.onDescriptionChange({ editor });
      },
    };
  }

  ngAfterViewInit(): void {
    if(this.form.mode === 1) {
      setTimeout(() => {
        this.fieldToFocus.nativeElement.focus();
        this.cdr.markForCheck();
      });
    }
  }

  openPlaceholderAutocomplete(): void {
    this.isPlaceholderAutocompleteOpen = true;
    setTimeout(() => {
      const placeholderInputElement = document.getElementById('placeholderAutocomplete');
      if (placeholderInputElement) {
        placeholderInputElement.focus();
      }
    }, 100);
  }

  onSelectPlaceholder({ code, id }: EmailPlaceholder): any {
    this.editor.insertPlaceholder(id);
    this.editor.editor.insertContent(`<span class="email-placeholder mceNonEditable">${PLACEHOLDER_CODE_SIGN}${code}</span>`);
    this.isPlaceholderAutocompleteOpen = false;
  }

  showPreview(): void {
    const { header, footer } = this;
    const body = this.editor.editor.getContent();
    const subject = getControlValue(this.form.group, 'subject');
    const templateName = getControlValue(this.form.group, 'name');
    this.dialogService
      .openWithInput(EmailTemplatePreviewComponent, <EmailTemplatePreviewDialogConfig>{
        header,
        footer,
        body,
        subject,
        templateName,
        type: this.type,
        officeId: this.officeId,
        masterZoneId: this.masterZoneId,
      }, {
        maxHeight: '92vh',
        width: '620px',
        maxWidth: '96vw',
      })
      .afterClosed()
      .subscribe();
  }

  loadMorePlaceholders($event: AutocompleteLoadMoreEvent): void {
    const { query, load } = $event;
    const templateTypeId = getControlValue(this.form.group, 'templateTypeId');
    load(this.businessService.findAllEmailPlaceholders(query, templateTypeId));
  }

  overwriteEmailTemplate(): void {
    const flow = this.pipelines.complete({
      execute: this.commonActions.execute((state: FormFlowState<EmailTemplateDetails>) => {
        if (this.needToCreateOverwrite(state.form.submitValue)) {
          return this.businessService.overwriteCreate(this.removeTechFields(state.form.submitValue));
        } else {
          return this.businessService.overwriteUpdate(this.removeTechFields(state.form.submitValue));
        }
      }),
      finish: () => {
        this.editor.cleanPlaceholders();
        this.router.navigateBack();
      },
    });

    const value = this.addPlaceholdersToState(this.form.group.getRawValue());
    flow.run({ form: this.form, trigger: 'process', value });
  }

  onDescriptionChange(event: any): void {
    if (this.isInitializing) {
      return;
    }
    
    const text = event.editor.getContent({ format: 'text' });
    const textHTML = event.editor.getContent();
    const originalText = this.body;
    const control = this.form.getControl('body');
    this.form.getControl('bodyText').setValue(text);
    this.form.getControl('bodyText').markAsPristine();
    if (!text || !text.trim().length) {
      control.setValue('');
    }
    if (textHTML === originalText || text === originalText || !control.dirty) {
      control.markAsPristine();
      return;
    }
    control.markAsDirty();
    control.markAsTouched();
    this.cdr.markForCheck();
  }

  private isAvailableCorporateTemplate(type: any): boolean {
    return type === EmailTemplateType.Internal
      || type === EmailTemplateType.Job
      || type === EmailTemplateType.System;
  }

  private isAvailableMasterTemplate(type: any): boolean {
    return type === EmailTemplateType.System || type === EmailTemplateType.Job;
  }

  /**
   * Provides information if User can Overwrite Email Template
   * @returns {boolean}
   */
  private checkCanOverwrite({ type }: EmailTemplateDetails): boolean {
    return (this.userContext.corporateView && this.isAvailableCorporateTemplate(type)) ||
      (this.userContext.masterView && this.isAvailableMasterTemplate(type));
  }

  /**
   * @param stateValue
   */
  private addPlaceholdersToState(stateValue: any): EmailTemplateDetails {
    return assign(stateValue, { placeholders: this.editor.getPlaceholders() });
  }

  private needToCreateOverwrite(stateValue: EmailTemplateDetails): boolean {
    const { masterZoneId, officeId } = stateValue;
    if (isNil(masterZoneId) && isNil(officeId) && this.userContext.corporateView) {
      return false;
    } else if (!isNil(masterZoneId) && isNil(officeId) && this.userContext.masterView) {
      return false;
    } else if (isNil(masterZoneId) && !isNil(officeId) && this.userContext.officeView) {
      return false;
    }
    return true;
  }

  private removeTechFields(values: any): any {
    return omit(values, 'bodyText');
  }
}
